/**
 * useMapInitialization Hook
 * 
 * Handles common initialization patterns for map components:
 * - Loading persisted map state from storage
 * - Handling URL hash-based navigation (e.g., #login after OAuth)
 * - Featured entity loading (HomePageMap only)
 * 
 * @module hooks/map/useMapInitialization
 */

import { useEffect } from 'react'
import { loadMapState } from '../../utils/mapValidation'
import { createLogger } from '../../utils/logger'
import { entityAPI } from '../../services/api'
import { trackEvent } from '../../utils/analytics'

const logger = createLogger('useMapInitialization')

/**
 * Initialize map state from storage
 * @param {Object} options - Initialization options
 * @param {Function} options.setEntities - Set entities function
 * @param {Function} options.setUserLocation - Set user location function
 * @param {Function} options.setRadius - Set radius function
 * @param {Function} options.setSearchLocation - Set search location function
 * @param {Function} options.setSearchLocationName - Set search location name function
 * @param {Function} options.getLastSavedLocation - Get last saved location function
 * @param {boolean} options.enabled - Whether to enable this initialization
 */
export const useMapStateInit = ({
  setEntities,
  setUserLocation,
  setRadius,
  setSearchLocation,
  setSearchLocationName,
  getLastSavedLocation,
  enabled = true
}) => {
  useEffect(() => {
    if (!enabled) return

    // If URL contains explicit lat/lng params (e.g. deeplink from city page),
    // skip restoring saved location so the URL coords take priority.
    const urlParams = new URLSearchParams(window.location.search)
    const urlLat = parseFloat(urlParams.get('lat'))
    const urlLng = parseFloat(urlParams.get('lng'))
    const hasUrlCoords = !isNaN(urlLat) && !isNaN(urlLng)

    const persistedState = loadMapState()
    const lastLocation = hasUrlCoords ? null : getLastSavedLocation()

    // When deeplink coords are present, seed userLocation from URL so entity
    // fetching happens at the right city, then bail — don't restore any saved state.
    if (hasUrlCoords) {
      setUserLocation({ latitude: urlLat, longitude: urlLng })
      if (persistedState?.radius) setRadius(persistedState.radius)
      return
    }
    
    // lastMapLocation (localStorage) contains timestamp of last search/GPS use
    // Prioritize it over persisted session state to remember user's last search
    if (lastLocation) {
      // Use lastMapLocation as the primary source of truth
      setUserLocation({
        latitude: lastLocation.latitude,
        longitude: lastLocation.longitude
      })
      // Set both searchLocation and searchLocationName so the input shows the saved address
      setSearchLocation(lastLocation.name || '')
      setSearchLocationName(lastLocation.name || '')
      
      // Still load other persisted state (entities, radius, etc.)
      if (persistedState) {
        if (persistedState.entities) setEntities(persistedState.entities)
        if (persistedState.radius) setRadius(persistedState.radius)
      }
    } else if (persistedState) {
      // Fallback to persisted state if no lastMapLocation exists
      if (persistedState.entities) setEntities(persistedState.entities)
      if (persistedState.userLocation) setUserLocation(persistedState.userLocation)
      if (persistedState.radius) setRadius(persistedState.radius)
      if (persistedState.searchLocation) setSearchLocation(persistedState.searchLocation)
      if (persistedState.searchLocationName) setSearchLocationName(persistedState.searchLocationName)
    }
  }, [getLastSavedLocation])
}

/**
 * Handle URL hash-based navigation (e.g., #login after OAuth)
 * @param {Object} options - Hash navigation options
 * @param {Function} options.onTabChange - Tab change callback
 * @param {boolean} options.isImmersiveMode - Whether in immersive mode
 * @param {boolean} options.enabled - Whether to enable hash navigation
 */
export const useHashNavigation = ({ 
  onTabChange, 
  isImmersiveMode,
  enabled = true 
}) => {
  useEffect(() => {
    if (!enabled) return

    const hash = window.location.hash.replace('#', '')
    if ((hash === 'auth' || hash === 'login') && onTabChange && isImmersiveMode) {
      onTabChange('auth')
      // Clean up the hash from URL
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }, [onTabChange, isImmersiveMode, enabled])
}

/**
 * Load and manage featured entity (HomePageMap only)
 * @param {Object} options - Featured entity options
 * @param {Function} options.setFeaturedEntity - Set featured entity function
 * @param {boolean} options.enabled - Whether to enable featured entity loading
 * @returns {Function} Load featured entity function
 */
export const useFeaturedEntity = ({ 
  setFeaturedEntity,
  enabled = true 
}) => {
  useEffect(() => {
    if (!enabled) return

    const loadFeaturedEntity = async () => {
      try {
        const response = await entityAPI.getFeaturedEntity()
        if (response.data.entity) {
          setFeaturedEntity(response.data.entity)
          logger.info('Featured entity loaded', response.data.entity)
        }
      } catch (error) {
        // Silent fail - featured entity is optional enhancement
        logger.error('Failed to load featured entity', error)
      }
    }
    loadFeaturedEntity()
  }, [setFeaturedEntity, enabled])
}

/**
 * Determine when to show featured entity with context-aware reasoning
 * @param {Object} options - Featured entity display options
 * @param {Object} options.featuredEntity - Featured entity object
 * @param {Object} options.hasInitialized - Whether map has initialized
 * @param {boolean} options.isGettingLocation - Whether currently getting location
 * @param {Object} options.userLocation - User location object
 * @param {Array} options.entities - All entities array
 * @param {Array} options.filteredEntities - Filtered entities array
 * @param {boolean} options.isLoading - Whether currently loading
 * @param {Function} options.setShowingFeaturedEntity - Set showing state function
 * @param {Function} options.setFeaturedEntityReason - Set reason function
 * @param {boolean} options.showingFeaturedEntity - Current showing state
 * @param {string} options.searchLocationName - Search location name
 */
export const useFeaturedEntityDisplay = ({
  featuredEntity,
  hasInitialized,
  isGettingLocation,
  userLocation,
  entities,
  filteredEntities,
  isLoading,
  setShowingFeaturedEntity,
  setFeaturedEntityReason,
  showingFeaturedEntity,
  searchLocationName
}) => {
  useEffect(() => {
    if (!featuredEntity || !hasInitialized || isGettingLocation) {
      setShowingFeaturedEntity(false)
      return
    }

    let shouldShow = false
    let reason = null

    // Condition 1: No user location (initial homepage load)
    if (!userLocation) {
      shouldShow = true
      reason = 'no-location'
    }
    // Condition 2: No entities found (search returned no results)
    else if (entities.length === 0 && !isLoading) {
      shouldShow = true
      reason = 'no-results'
    }
    // Condition 3: All entities filtered out
    else if (filteredEntities.length === 0 && entities.length > 0) {
      shouldShow = true
      reason = 'filtered-out'
    }

    if (shouldShow !== showingFeaturedEntity) {
      setShowingFeaturedEntity(shouldShow)
      setFeaturedEntityReason(reason)
      
      if (shouldShow) {
        // Track impression with location context
        trackEvent('Featured Entity', 'impression', featuredEntity.id, { 
          reason,
          searchLocation: searchLocationName || 'unknown',
          userLatitude: userLocation?.latitude || null,
          userLongitude: userLocation?.longitude || null
        })
      }
    }
  }, [
    userLocation, 
    entities, 
    filteredEntities, 
    isLoading, 
    featuredEntity, 
    showingFeaturedEntity, 
    hasInitialized, 
    isGettingLocation,
    setShowingFeaturedEntity,
    setFeaturedEntityReason,
    searchLocationName
  ])
}
